import { Fragment, useEffect, useState } from 'react'
import { api, auth } from '../lib/api.js'
import './follow-ups.css'

// 运营跟进提醒（喇叭）：签收后 7 天内没进线的订单，提醒运营今天要打电话引导进线的车牌。
// 运营只看自己名下的；管理员多「日报」「周报」两页。数据来自车网通备注时间线，后端上班时间每半小时拉一次。
const POLL_MS = 5 * 60 * 1000
const md = day => (day ? `${Number(day.slice(5, 7))}.${Number(day.slice(8, 10))}` : '')
const beijing = (date, opts) => date.toLocaleString('en-GB', { timeZone: 'Asia/Shanghai', hour12: false, ...opts })

export function useFollowUps(agentId) {
  const [data, setData] = useState(null)
  useEffect(() => {
    if (!agentId || auth.isAftersales) return
    let alive = true
    const load = () => api.listFollowUps().then(d => alive && setData(d)).catch(() => {})   // 角标失败不打扰使用
    load()
    const timer = setInterval(load, POLL_MS)
    return () => { alive = false; clearInterval(timer) }
  }, [agentId])
  return data?.available ? data : null
}

// 运营每天第一次打开（北京时间 8:30 以后）且有待联系订单时自动弹一次；记在本机浏览器，换设备会再弹一次
export function shouldAutoOpen(data, agentId) {
  if (!data || auth.isAdmin || !data.pending) return false
  const [h, m] = beijing(new Date(), { hour: '2-digit', minute: '2-digit' }).split(':').map(Number)
  if (h * 60 + m < 8 * 60 + 30) return false
  const key = `etc_followup_popup_${agentId}`
  try {
    if (localStorage.getItem(key) === data.date) return false
    localStorage.setItem(key, data.date)
  } catch { /* 无痕模式等拿不到本地存储时照常弹，不影响使用 */ }
  return true
}

export function FollowUpPanel({ data, onClose }) {
  const [tab, setTab] = useState('list')
  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal fu-modal">
        <div className="spread">
          <h2 style={{ margin: 0 }}>跟进提醒</h2>
          <button className="btn-link" onClick={onClose}>关闭</button>
        </div>
        {auth.isAdmin && (
          <div className="fu-tabs">
            {[['list', '待联系'], ['daily', '日报'], ['weekly', '周报']].map(([k, label]) => (
              <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{label}</button>
            ))}
          </div>
        )}
        {tab === 'list' && <PendingList data={data} />}
        {tab === 'daily' && <Report render={r => <Daily report={r} />} />}
        {tab === 'weekly' && <Report render={r => <Weekly report={r} />} />}
      </div>
    </div>
  )
}

function Freshness({ data }) {
  if (!data?.capturedAt) return null
  return (
    <div className="muted small fu-fresh">
      数据更新于 {beijing(new Date(data.capturedAt), { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
      （上班时间每半小时同步车网通）
      {data.error && <span className="fu-warn">　最近一次同步失败，显示的是上次结果</span>}
    </div>
  )
}

function RemarkHelp() {
  return (
    <details className="fu-help">
      <summary>联系后备注怎么写</summary>
      <ul>
        <li>联系客户后，在车网通订单备注里写：<b>日期＋情况</b>。例：<code>10.11联系了等有空</code>、<code>10.12未接</code>、<code>10.13拒接</code>。</li>
        <li>日期写在最前面，<code>10.11</code> 或 <code>10月11日</code> 都行。系统按这个日期记为已联系，不看你什么时候写的；没写日期的备注不算联系，会继续提醒。</li>
        <li>日期不能晚于写备注的当天，写错的不算。要用「运营」或「售后客服」身份写，客服、团队写的不算。</li>
        <li>下一次该联系的日子 = 这次备注的日期 + 2 天，最多提醒到第 4 次；签收满 7 天（且不跨月）就不再提醒。</li>
        <li>客户进线了写 <code>10.11进线</code>（日期＋进线），这单就不再提醒。联系时要提醒客户进线的，别直接写成「日期＋进线」，写 <code>10.11通知进线</code>，否则会被当成客户已进线。</li>
      </ul>
    </details>
  )
}

function PendingList({ data }) {
  const [owner, setOwner] = useState('')
  const [view, setView] = useState(data)
  useEffect(() => {
    if (!owner) { setView(data); return }
    let alive = true
    api.listFollowUps({ agentId: owner }).then(d => alive && setView(d)).catch(() => {})
    return () => { alive = false }
  }, [owner, data])
  const owners = [...new Map([...data.overdue, ...data.today, ...data.tomorrow, ...data.contacted]
    .filter(x => x.ownerId).map(x => [x.ownerId, x.ownerName])).entries()]
  const groups = [
    ['overdue', '已逾期', x => `逾期 ${x.overdue} 天`, 'fu-red'],
    ['today', '今天联系', () => '今天', ''],
    ['tomorrow', '明天到期', () => '明天', 'muted'],
    ['contacted', '今天已联系', () => '已联系', 'muted'],
  ]
  return (
    <>
      <div className="fu-tip">请今天电话联系，引导客户进线。联系后在车网通写一条备注：日期＋情况，例：10.11联系了等有空。</div>
      <RemarkHelp />
      <Freshness data={view} />
      {auth.isAdmin && (
        <select className="input fu-owner" value={owner} onChange={e => setOwner(e.target.value)}>
          <option value="">全部运营</option>
          {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      )}
      {!view.pending && !view.tomorrow.length && !view.contacted.length && <div className="empty">今天没有需要联系的订单</div>}
      {groups.map(([key, title, when, cls]) => view[key].length > 0 && (
        <div key={key} className="fu-group">
          <div className={`fu-group-title ${cls}`}>{title}（{view[key].length}）</div>
          <div className="fu-scroll">
            <table className="fu-table">
              <thead><tr><th>车牌</th><th>手机号</th><th>第几次</th><th>上次联系</th><th>状态</th>{auth.isAdmin && <th>运营</th>}</tr></thead>
              <tbody>
                {view[key].map(x => (
                  <tr key={x.id}>
                    <td><b>{x.plate}</b></td><td className="mono">{x.phone}</td><td>第{x.k}次</td>
                    <td>{md(x.lastContact)}</td><td className={cls}>{when(x)}</td>{auth.isAdmin && <td>{x.ownerName}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  )
}

function Report({ render }) {
  const [report, setReport] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    api.getFollowUpReport().then(setReport).catch(e => setErr(e.message))
  }, [])
  if (err) return <div className="err">{err}</div>
  if (!report) return <div className="empty">加载中…</div>
  if (!report.available) return <div className="empty">订单同步未开启，暂无数据</div>
  return <><Freshness data={report} />{render(report)}</>
}

// 汇总表：每位运营一行，点一行展开未联系的车牌；最下面一行合计
function SummaryTable({ columns, report, detail }) {
  const [open, setOpen] = useState(null)
  const span = columns.length + 1
  return (
    <div className="fu-scroll">
      <table className="fu-table">
        <thead><tr><th>运营</th>{columns.map(([, label]) => <th key={label}>{label}</th>)}</tr></thead>
        <tbody>
          {report.rows.map(r => {
            const key = r.ownerId || r.ownerName
            return (
              <Fragment key={key}>
                <tr className={r.items.length ? 'fu-click' : ''} onClick={() => r.items.length && setOpen(open === key ? null : key)}>
                  <td>{r.items.length > 0 && (open === key ? '▾ ' : '▸ ')}{r.ownerName}</td>
                  {columns.map(([k, , cls]) => <td key={k} className={r[k] ? cls : ''}>{r[k]}</td>)}
                </tr>
                {open === key && (
                  <tr className="fu-detail"><td colSpan={span}>
                    {r.items.map(x => <div key={`${x.id}-${x.k || x.kind}`}>{detail(x)}</div>)}
                  </td></tr>
                )}
              </Fragment>
            )
          })}
          {!report.rows.length && <tr><td colSpan={span} className="muted">没有需要联系的订单</td></tr>}
          <tr className="fu-total"><td>合计</td>{columns.map(([k]) => <td key={k}>{report.total[k]}</td>)}</tr>
        </tbody>
      </table>
    </div>
  )
}

const plateLine = x => <><b>{x.plate}</b>　<span className="mono">{x.phone}</span>　</>

function Daily({ report }) {
  const columns = [['need', '需联系'], ['contacted', '已联系'], ['missed', '未联系', 'fu-red']]
  const detail = x => <>{plateLine(x)}第{x.k}次　{x.overdue > 0 ? <span className="fu-red">逾期 {x.overdue} 天</span> : '当天到期'}</>
  return (
    <>
      <div className="fu-tip">需联系 = 当天到期 + 之前逾期还没联系的；已联系 = 备注开头写的日期是当天（格式：日期＋情况，例：10.11联系了等有空）。点运营那一行看未联系的车牌。</div>
      {[['today', '今天'], ['yesterday', '昨天']].map(([k, label]) => (
        <div key={k} className="fu-group">
          <div className="fu-group-title">{label}（{md(report.daily[k].day)}）</div>
          <SummaryTable columns={columns} report={report.daily[k]} detail={detail} />
        </div>
      ))}
    </>
  )
}

function Weekly({ report }) {
  const [which, setWhich] = useState('lastWeek')
  const week = report.weekly[which]
  const columns = [['need', '应联系'], ['onTime', '按时联系'], ['late', '逾期才联系', 'fu-red'],
    ['missed', '未联系', 'fu-red'], ['expired', '过7天未进线', 'fu-red']]
  const detail = x => x.kind === 'expired'
    ? <>{plateLine(x)}签收 {md(x.signDay)}，7 天内未进线（运营联系过 {x.contacts} 天）</>
    : <>{plateLine(x)}第{x.k}次　该联系 {md(x.due)}　{x.kind === 'late' ? `${md(x.contact)} 才联系` : '未联系'}</>
  return (
    <>
      <div className="fu-tabs small-tabs">
        {[['lastWeek', '上周'], ['thisWeek', '本周']].map(([k, label]) => (
          <button key={k} className={which === k ? 'on' : ''} onClick={() => setWhich(k)}>{label}</button>
        ))}
      </div>
      <div className="fu-tip">{md(week.from)}–{md(week.to)}。每次提醒按「该联系日」归到那一周，统计截至该周最后一天（本周截至今天），之后补打的不改旧周数字；本周的未联系含今天还没打的。</div>
      <SummaryTable columns={columns} report={week} detail={detail} />
    </>
  )
}
